import { useEffect, useMemo, useState } from 'react'
import { Download, Edit, Eye, Plus, RefreshCw, Trash2 } from 'lucide-react'
import apiClient from '../../api/axiosClient'
import { useAuth } from '../../contexts/AuthContext'
import ReceiptEditor from './ReceiptEditor'

const formatDate = (value) => {
  if (!value) return ''
  try {
    return new Date(value).toISOString().split('T')[0]
  } catch {
    return ''
  }
}

const formatTime = (value) => {
  if (!value) return ''
  return String(value).slice(0, 5)
}

const getLatestAdmission = (admissions = []) => {
  const validAdmissions = admissions.filter((admission) => {
    return admission.date_of_admission || admission.date_of_discharge || admission.ip_no || admission.admission_id
  })

  return validAdmissions[validAdmissions.length - 1] || {}
}

const IPFinalBill = ({ patient, onGenerated }) => {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [listLoading, setListLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [ipFinalBills, setIpFinalBills] = useState([])
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [editingBill, setEditingBill] = useState(null)

  const latestAdmission = useMemo(() => getLatestAdmission(patient?.admissions || []), [patient])

  const [formData, setFormData] = useState({
    bill_date: new Date().toISOString().split('T')[0],
    bill_time: new Date().toTimeString().slice(0, 5),
    admission_no: '',
    patient_name: '',
    doa: '',
    doa_time: '',
    consultant: '',
    referred_by: '',
    bill_no: '',
    uhid_no: '',
    ward_no: '',
    dod: '',
    dod_time: '',
    department: '',
    age_sex: '',
    items: [{ sl_no: 1, particular: 'OP', amount: '0.00' }],
    remarks: ''
  })

  useEffect(() => {
    if (!patient) return

    setFormData((prev) => ({
      ...prev,
      admission_no: latestAdmission.admission_id || latestAdmission.ip_no || patient.ip_no || '',
      patient_name: patient.title ? `${patient.title} ${patient.full_name || ''}`.trim() : patient.full_name || '',
      doa: formatDate(latestAdmission.date_of_admission),
      doa_time: formatTime(latestAdmission.time_of_admission),
      consultant: latestAdmission.surgeon || latestAdmission.consultant || '',
      referred_by: latestAdmission.referred_by || patient.referred_by || '',
      uhid_no: patient.uhid_no || '',
      ward_no: latestAdmission.ward_no || latestAdmission.ward || '',
      dod: formatDate(latestAdmission.date_of_discharge),
      dod_time: formatTime(latestAdmission.time_of_discharge),
      department: latestAdmission.department || latestAdmission.speciality || latestAdmission.body_parts || '',
      age_sex: [patient.age ? `${patient.age}Y` : '', patient.gender || ''].filter(Boolean).join('/'),
      remarks: latestAdmission.detail_of_procedure || latestAdmission.treatment_planned || prev.remarks
    }))
  }, [patient, latestAdmission])

  const loadIPFinalBills = async () => {
    if (!patient?.patient_id && !patient?.personal_id) return

    try {
      setListLoading(true)
      const response = await apiClient.get('/insurance/ip-final-bills', {
        params: {
          page: 1,
          per_page: 10,
          personal_id: patient.patient_id || patient.personal_id
        }
      })
      setIpFinalBills(response.data.receipts || [])
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load IP final bills')
    } finally {
      setListLoading(false)
    }
  }

  useEffect(() => {
    loadIPFinalBills()
  }, [patient?.patient_id, patient?.personal_id])

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { sl_no: prev.items.length + 1, particular: '', amount: '' }]
    }))
  }

  const removeItem = (index) => {
    setFormData((prev) => {
      if (prev.items.length === 1) return prev
      const items = prev.items
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({ ...item, sl_no: itemIndex + 1 }))
      return { ...prev, items }
    })
  }

  const updateItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items]
      items[index] = { ...items[index], [field]: value }
      return { ...prev, items }
    })
  }

  const totalAmount = formData.items.reduce((sum, item) => {
    return sum + (parseFloat(item.amount) || 0)
  }, 0)

  const buildNarration = () => {
    return [
        'IP FINAL BILL',
        formData.bill_no ? `Bill No: ${formData.bill_no}` : '',
        formData.admission_no ? `Adm No: ${formData.admission_no}` : '',
        formData.ward_no ? `Ward No: ${formData.ward_no}` : '',
        formData.doa ? `DOA: ${formData.doa} ${formData.doa_time || ''}`.trim() : '',
        formData.dod ? `DOD: ${formData.dod} ${formData.dod_time || ''}`.trim() : '',
        formData.consultant ? `Consultant: ${formData.consultant}` : '',
        formData.department ? `Department: ${formData.department}` : '',
        formData.referred_by ? `Referred By: ${formData.referred_by}` : '',
        formData.age_sex ? `Age/Sex: ${formData.age_sex}` : '',
        formData.remarks ? `Remarks: ${formData.remarks}` : ''
      ].filter(Boolean).join(' | ')
  }

  const handleShowPreview = () => {
    setError('')
    setSuccess('')

    const hasEmptyItems = formData.items.some((item) => !item.particular || !item.amount)
    if (hasEmptyItems) {
      setError('All items must have particulars and amount')
      return
    }

    setPreviewData({
      receipt_date: formData.bill_date,
      receipt_time: formData.bill_time,
      items: formData.items.map((item, index) => ({
        sl_no: index + 1,
        particular: item.particular || 'IP FINAL BILL',
        amount: item.amount || '0'
      })),
      narration: buildNarration(),
      total_amount: totalAmount
    })
    setShowPreview(true)
  }

  const handleConfirmAndGenerate = async (receiptId, updatedData) => {
    try {
      setLoading(true)
      setError('')
      setSuccess('')

      const response = await apiClient.post(
        `/insurance/patients/${patient.patient_id || patient.personal_id}/generate-receipt`,
        updatedData,
        { responseType: 'blob' }
      )

      setSuccess('IP Final Bill generated successfully.')
      setShowPreview(false)
      setPreviewData(null)
      setIsOpen(false)
      loadIPFinalBills()
      onGenerated?.()
    } catch (err) {
      console.error('IP final bill generation error:', err)
      setError(err.response?.data?.error || err.message || 'Failed to generate IP final bill')
    } finally {
      setLoading(false)
    }
  }

  if (!patient) return null

  const billDetailFields = [
    { label: 'Bill No', field: 'bill_no', placeholder: 'Enter bill no' },
    { label: 'Adm No', field: 'admission_no', placeholder: 'Admission no' },
    { label: 'Pt Name', field: 'patient_name', placeholder: 'Patient name' },
    { label: 'DOA', field: 'doa', type: 'date' },
    { label: 'DOA Time', field: 'doa_time', type: 'time' },
    { label: 'UMR / UHID No', field: 'uhid_no', placeholder: 'UHID no' },
    { label: 'Ward No', field: 'ward_no', placeholder: 'Ward no' },
    { label: 'DOD', field: 'dod', type: 'date' },
    { label: 'DOD Time', field: 'dod_time', type: 'time' },
    { label: 'Consultant', field: 'consultant', placeholder: 'Consultant name' },
    { label: 'Department', field: 'department', placeholder: 'Department' },
    { label: 'Referred By', field: 'referred_by', placeholder: 'Referred by' },
    { label: 'Age / Sex', field: 'age_sex', placeholder: 'Age / Sex' }
  ]

  const handleViewBill = async (receiptId) => {
    try {
      const response = await apiClient.get(`/insurance/receipts/${receiptId}/download`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      if (user?.role === 'super_admin') {
        await apiClient.post(`/insurance/ip-final-bills/${receiptId}/viewed`)
        setIpFinalBills((prev) => prev.map((bill) => (
          bill.receipt_id === receiptId
            ? { ...bill, viewed_by_current_user: true }
            : bill
        )))
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to view IP final bill')
    }
  }

  const handleDownloadBill = async (receipt) => {
    try {
      const response = await apiClient.get(`/insurance/receipts/${receipt.receipt_id}/download`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `IP_Final_Bill_${receipt.receipt_no}_${receipt.receipt_date}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to download IP final bill')
    }
  }

  const handleSaveBill = async (receiptId, updateData) => {
    try {
      await apiClient.put(`/insurance/receipts/${receiptId}`, updateData)
      setEditingBill(null)
      loadIPFinalBills()
    } catch (err) {
      throw err
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Generate Receipt</h2>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            isOpen
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isOpen ? 'Close' : 'Open Receipt Form'}
        </button>
      </div>

      {isOpen && (
        <div className="space-y-4 mt-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Static Info Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Receipt Information</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">Patient:</span> {formData.patient_name || patient.full_name}</div>
              <div><span className="font-medium">UHID:</span> {formData.uhid_no || patient.uhid_no}</div>
              <div><span className="font-medium">Center:</span> {patient.center_code}</div>
              <div><span className="font-medium">Mobile:</span> {patient.mobile}</div>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bill Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.bill_date}
                onChange={(e) => updateField('bill_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bill Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.bill_time}
                onChange={(e) => updateField('bill_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Editable IP Bill Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              IP Final Bill Details
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {billDetailFields.map((item) => (
                <div key={item.field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {item.label}
                  </label>
                  <input
                    type={item.type || 'text'}
                    value={formData[item.field]}
                    onChange={(e) => updateField(item.field, e.target.value)}
                    placeholder={item.placeholder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Receipt Items <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <Plus size={16} />
                <span>Add Item</span>
              </button>
            </div>

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 w-16">SI No</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Particulars</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 w-40">Amount</th>
                    <th className="px-4 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {formData.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-center text-gray-700">{item.sl_no}</td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={item.particular}
                          onChange={(e) => updateItem(index, 'particular', e.target.value)}
                          placeholder="Particulars"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.amount}
                          onChange={(e) => updateItem(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-700"
                            title="Remove item"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="2" className="px-4 py-3 text-right">Total Amount:</td>
                    <td className="px-4 py-3">{totalAmount.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => updateField('remarks', e.target.value)}
              rows={3}
              placeholder="Treatment, procedure, or final bill remarks"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading || totalAmount === 0}
              onClick={handleShowPreview}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              <span>Review & Generate</span>
            </button>
          </div>
        </div>
      )}

      {showPreview && previewData && (
        <ReceiptEditor
          receipt={{
            receipt_no: 'NEW',
            receipt_id: null,
            receipt_date: previewData.receipt_date,
            receipt_time: previewData.receipt_time,
            items: previewData.items,
            narration: previewData.narration,
            total_amount: previewData.total_amount
          }}
          patient={patient}
          isPreview={true}
          previewActionLabel="Confirm"
          onClose={() => {
            setShowPreview(false)
            setPreviewData(null)
          }}
          onSave={handleConfirmAndGenerate}
        />
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Generated IP Final Bills</h3>
          <button
            type="button"
            onClick={loadIPFinalBills}
            disabled={listLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={listLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Receipt No</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Items</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-600">
                    Loading IP final bills...
                  </td>
                </tr>
              ) : ipFinalBills.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-600">
                    No IP final bills found
                  </td>
                </tr>
              ) : (
                ipFinalBills.map((bill) => (
                  <tr key={bill.receipt_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{bill.receipt_no}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="text-gray-900">{bill.receipt_date}</div>
                        <div className="text-gray-500">{bill.receipt_time}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-900">{bill.total_amount?.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{bill.items?.length || 0} item(s)</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewBill(bill.receipt_id)}
                          disabled={user?.role === 'super_admin' && bill.viewed_by_current_user}
                          className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={
                            user?.role === 'super_admin' && bill.viewed_by_current_user
                              ? 'You have already viewed this bill. If you need to access it again, please contact the Insurance Manager'
                              : 'View PDF'
                          }
                        >
                          <Eye size={16} />
                        </button>
                        {user?.role === 'super_admin' && bill.viewed_by_current_user && (
                          <span className="text-xs text-red-600">
                            You have already viewed this bill. If you need to access it again, please contact the Insurance Manager
                          </span>
                        )}
                        {user?.role === 'cmo' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleDownloadBill(bill)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                              title="Download PDF"
                            >
                              <Download size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingBill(bill)}
                              className="p-1 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                              title="Edit Bill"
                            >
                              <Edit size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingBill && (
        <ReceiptEditor
          receipt={editingBill}
          patient={patient}
          onClose={() => setEditingBill(null)}
          onSave={handleSaveBill}
        />
      )}
    </div>
  )
}

export default IPFinalBill
